import { IDay, IDayWatchData, IDayWatchGroup, IShipUser, IShipUserLevel } from "../../Types"

/**
 * Whether the signed-in user may **clear** an alert.
 *
 * Snooze and acknowledge are open to every ship user; only clearing is gated
 * (see `AlertApi.swift`), so this is the one permission the alert dialog needs.
 *
 * ## Why this is computed on the client
 *
 * The answer depends on the ship-local **hour**: it changes every time the watch
 * rotates. That rules out shipping it as a flag — the session response is fetched
 * at login and the alert envelope is written once when the alert fires and shared
 * by every device, so either would be stale within hours. Recomputing here keeps
 * the button in step with the watch plan for free.
 *
 * The server still enforces the rule (`userAuthorizedToClear`); this only decides
 * whether to *offer* the button, so a disagreement degrades to a rejected command
 * rather than an unauthorised clear. Both implementations are pinned to the same
 * cases in `Tests/Fixtures/alert-clear-permission.json` — change the rule in one
 * place and that table fails the other.
 *
 * ## The id-space trap
 *
 * `accountUserId` is an account `User.id`, but `day.skipper` and the watch groups'
 * `people` hold **`ShipUser.id`** values. The two spaces overlap by coincidence, so
 * the account user must be mapped to this ship's `ShipUser` before any comparison.
 */
export function mayClearAlert({
  accountUserId,
  shipUsers,
  day,
  shipLocalHour,
}: {
  accountUserId?: number;
  shipUsers: IShipUser[];
  day?: IDay;
  shipLocalHour: number;
}): boolean {
  if (accountUserId === undefined || !day) return false;
  const shipUser = shipUsers.find((u) => u.userId === accountUserId);
  if (!shipUser) return false;

  const skipper = day.skipper ?? undefined;
  if (skipper !== undefined && skipper === shipUser.id) return true;

  const onWatch = watchGroupAtHour(dayWatch(day), shipLocalHour);
  if (onWatch?.people?.includes(shipUser.id)) return true;

  // #1146: a day with neither a skipper nor a covering watch group has nobody for
  // the gate to defer to, so fall back to the ship permission level rather than
  // leaving the alert permanently unclearable.
  if (skipper === undefined && !onWatch) {
    return shipUser.shipUserLevel >= IShipUserLevel.editCurrent;
  }
  return false;
}

/**
 * Whether the signed-in user is the day's skipper.
 *
 * Used to hide "escalate to skipper" from the skipper themselves, for whom it
 * would be a no-op. Same id-space trap as `mayClearAlert`: `day.skipper` holds a
 * **`ShipUser.id`**, so the account user must be mapped first.
 */
export function isShipSkipper({
  accountUserId,
  shipUsers,
  day,
}: {
  accountUserId?: number;
  shipUsers: IShipUser[];
  day?: IDay;
}): boolean {
  if (accountUserId === undefined || !day || day.skipper === undefined) return false;
  return shipUsers.find((u) => u.userId === accountUserId)?.id === day.skipper;
}

function dayWatch(day: IDay): IDayWatchData | undefined {
  if (!day.data) return undefined;
  try {
    return (JSON.parse(day.data) as { watch?: IDayWatchData }).watch;
  } catch {
    return undefined;
  }
}

/**
 * The group on watch at `hour`, resolved through the covering shift's `group`
 * reference. Mirrors `DayWatchData.group(atHour:)`, including its treatment of a
 * dangling group reference as "nobody on watch".
 */
function watchGroupAtHour(watch: IDayWatchData | undefined, hour: number): IDayWatchGroup | undefined {
  if (!watch) return undefined;
  const shift = watch.shifts?.find((s) => shiftCovers(s.start, s.duration, normaliseHour(hour)));
  if (!shift?.group) return undefined;
  return watch.groups?.find((g) => g.id === shift.group);
}

/** Mirrors `DayWatchShift.covers(hour:)` — wraps past midnight, 24h+ spans all day. */
function shiftCovers(start: number, duration: number, hour: number): boolean {
  const from = normaliseHour(start);
  const span = Math.max(0, duration);
  if (span >= 24) return true;
  const end = from + span;
  if (end <= 24) return hour >= from && hour < end;
  return hour >= from || hour < end - 24;
}

function normaliseHour(hour: number): number {
  return ((hour % 24) + 24) % 24;
}

/**
 * The hour (0–23) at the ship's local time, from a day's stored UTC offset in
 * seconds. Mirrors `AlertEngineApi.shipLocalHour` — the watch plan is written in
 * ship-local hours, so resolving the on-watch group in the browser's own timezone
 * would put the wrong crew on watch for any boat that isn't in it.
 */
export function shipLocalHour(timezone: number, now: Date = new Date()): number {
  return Math.floor(((now.getTime() / 1000 + timezone) % 86400 + 86400) % 86400 / 3600);
}
